import { useSelector } from "react-redux";
import { RootState } from "../../redux/store/store";

// Permission types
export interface Permission {
  view: boolean;
  viewAll: boolean;
  update: boolean;
  create: boolean;
  delete: boolean;
}

export interface Permissions {
  [key: string]: Permission;
}

// Permission mapping for different features
export const PERMISSION_MAPPING = {
  // Admin Module
  "Admin Dashboard": "ADMIN_DASHBOARD",
  "Admin Settings": "ADMIN_SETTINGS",
  "Admin Account User": "ADMIN_USERS",
  "Admin Account Payment source": "ADMIN_ACCOUNT_PAYMENT_SOURCE",
  "Admin Form Builder": "ADMIN_FORM_BUILDER",
  "Admin Membership Plan": "ADMIN_MEMBERSHIP",
  "Admin Reports": "ADMIN_REPORTS",
  "Admin Educational Material": "ADMIN_EDUCATION_MATERIALS",
  "Admin Affiliate": "ADMIN_AFFILIATE",
  "Admin Roles and Permissions": "ADMIN_ROLES_PERMISSIONS",
  "Admin Notifications": "ADMIN_NOTIFICATIONS",
  "Admin Charge and Taxes": "ADMIN_CHARGE_TAXES",
  "Admin Providers": "ADMIN_PROVIDERS",
  "Admin Providers and locations": "ADMIN_PROVIDERS_LOCATIONS",
  "Admin Location Groups": "ADMIN_LOCATION_GROUPS",
  "Admin Locations": "ADMIN_LOCATIONS",
  "Admin Maps": "ADMIN_MAPS",
  "Admin Coupons - Retail patient": "ADMIN_COUPONS",
  "Admin Terms and conditions": "ADMIN_TERMS_CONDITIONS",
  "Admin Audit Log": "ADMIN_AUDIT_LOG",
  "Admin Cancellation Reason": "ADMIN_CANCELLATION_REASON",
  "Admin Coverage Plan": "ADMIN_COVERAGE_PLAN",
  "Admin Invoicing Ahead of Time": "ADMIN_INVOICING_AHEAD",

  // Affiliate Portal Module
  "Affiliate Portal": "AFFILIATE_PORTAL",
  "Affiliate Dashboard": "AFFILIATE_DASHBOARD",
  "Affiliate Providers": "AFFILIATE_PROVIDERS",
  "Affiliate Locations": "AFFILIATE_LOCATIONS",
  "Affiliate Location Groups": "AFFILIATE_LOCATION_GROUPS",
  "Affiliate Maps": "AFFILIATE_MAPS",
  "Affiliate Charge Categories": "AFFILIATE_CHARGE_CATEGORIES",
  "Affiliate Settings": "AFFILIATE_SETTINGS",
  "Affiliate Settings Providers": "AFFILIATE_SETTINGS_PROVIDERS",
  "Affiliate Settings Notification Preference":
    "AFFILIATE_SETTINGS_NOTIFICATION_PREFERENCE",
  "Affiliate Settings Terms": "AFFILIATE_SETTINGS_TERMS",
  "Affiliate Settings Profile": "AFFILIATE_SETTINGS_PROFILE",
  "Affiliate Bills": "AFFILIATE_BILLS",
  "Affiliate Patients": "AFFILIATE_PATIENTS",
  "Affiliate Linked Providers": "AFFILIATE_LINKED_PROVIDERS",
  "Affiliate Linked Locations": "AFFILIATE_LINKED_LOCATIONS",

  // EHR Module
  "EHR Dashboard": "EHR_DASHBOARD",
  "EHR Patients": "EHR_PATIENTS",
  "EHR Patient Directory": "EHR_PATIENT_DIRECTORY",
  "EHR Patient Profile": "EHR_PATIENT_PROFILE",
  "EHR Patient Dashboard": "EHR_PATIENT_DASHBOARD",
  "EHR Patient Educational Material": "EHR_PATIENT_EDUCATION",
  "EHR Patient allergies": "EHR_PATIENT_ALLERGIES",
  "EHR Patient Vitals": "EHR_PATIENT_VITALS",
  "EHR Patient Medications": "EHR_PATIENT_MEDICATIONS",
  "EHR Patient Diagnosis": "EHR_PATIENT_DIAGNOSIS",
  "EHR Patient Vaccines": "EHR_PATIENT_VACCINES",
  "EHR Patient Imaging": "EHR_PATIENT_IMAGING",
  "EHR Patient Labs": "EHR_PATIENT_LABS",
  "EHR Patient Referrals": "EHR_PATIENT_REFERRALS",
  "EHR Patient Documents": "EHR_PATIENT_DOCUMENTS",
  "EHR Patient Messages": "EHR_PATIENT_MESSAGES",
  "EHR Patient Tasks": "EHR_PATIENT_TASKS",
  "EHR Patient History": "EHR_PATIENT_HISTORY",
  "EHR Patient Visit Details": "EHR_PATIENT_VISIT_DETAILS",
  "EHR Patient timeline": "EHR_PATIENT_TIMELINE",
  "EHR Patient Drug Tolerance": "EHR_PATIENT_DRUG_TOLERANCE",
  "EHR Patient Pharmacies": "EHR_PATIENT_PHARMACIES",
  "EHR Patient Invoice": "EHR_PATIENT_INVOICE",
  "EHR Patient Membership Details": "EHR_PATIENT_MEMBERSHIP",
  "EHR Scheduling": "EHR_SCHEDULING",
  "EHR Appointment type": "EHR_APPOINTMENT_TYPE",
  "EHR Availabillity": "EHR_AVAILABILITY",
  "EHR Tasks": "EHR_TASKS",
  "EHR Referral": "EHR_REFERRAL",
  "EHR Status Configuration": "EHR_STATUS_CONFIG",
  "EHR Customizable Information Sharing": "EHR_CUSTOMIZABLE_INFO_SHARING",
  "EHR Admin": "EHR_ADMIN",
  "EHR Settings": "EHR_SETTINGS",

  // Billing Module
  Billing: "BILLING",
  "Billing Basic Details": "BILLING_BASIC_DETAILS",
  "Billing Dashboard": "BILLING_DASHBOARD",
  "Billing Patients": "BILLING_PATIENTS",
  "Billing All Patients": "BILLING_ALL_PATIENTS",
  "Billing Employers": "BILLING_EMPLOYERS",
  "Billing Employer": "BILLING_EMPLOYER",
  "Billing Employees": "BILLING_EMPLOYEES",
  "Billing Employee Invoices": "BILLING_EMPLOYEE_INVOICES",
  "Billing Invoices": "BILLING_INVOICES",
  "Billing Invoice Settings": "BILLING_INVOICE_SETTINGS",
  "Billing Employee Payment Option": "BILLING_EMPLOYEE_PAYMENT_OPTION",
  "Billing Payment Option": "BILLING_PAYMENT_OPTION",
  "Billing Plans": "BILLING_PLANS",
  "Billing Pricing": "BILLING_PRICING",
  "Billing Credits": "BILLING_CREDITS",
  "Billing Memberships": "BILLING_MEMBERSHIPS",
  "Billing employee Notes": "BILLING_EMPLOYEE_NOTES",
  "Billing employee Notifications": "BILLING_EMPLOYEE_NOTIFICATIONS",
  "Billing Notifications": "BILLING_NOTIFICATIONS",
  "Billing Notes": "BILLING_NOTES",
  "Billing Brokers": "BILLING_BROKERS",
  "Billing Retail": "BILLING_RETAIL",

  // Employer Management Module
  "Employer Management": "EMPLOYER_MANAGEMENT",
  "EM Dashboard": "EM_DASHBOARD",
  "EM Employers": "EM_EMPLOYERS",
  "EM All employees": "EM_ALL_EMPLOYEES",
  "EM Employees": "EM_EMPLOYEES",
  "EM Basic Details": "EM_BASIC_DETAILS",
  "EM Account Details": "EM_ACCOUNT_DETAILS",
  "EM Credits": "EM_CREDITS",
  "EM Invoices": "EM_INVOICES",
  "EM Invoice Settings": "EM_INVOICE_SETTINGS",
  "EM Employee Payment Option": "EM_EMPLOYEE_PAYMENT_OPTION",
  "EM Payment Option": "EM_PAYMENT_OPTION",
  "EM Plans": "EM_PLANS",
  "EM Pricing": "EM_PRICING",
  "EM Memberships": "EM_MEMBERSHIPS",
  "EM Notifications": "EM_NOTIFICATIONS",
  "EM Notes": "EM_NOTES",
  "EM Employee Notifications": "EM_EMPLOYEE_NOTIFICATIONS",
  "EM Brokers": "EM_BROKERS",
  "EM Employee Uploads": "EM_EMPLOYEE_UPLOADS",
  "EM Online Signup": "EM_ONLINE_SIGNUP",

  // Employer Portal Module
  "Employer Portal": "EMPLOYER_PORTAL",
  "EMP Dashboard": "EMP_DASHBOARD",
  "EMP All employees": "EMP_ALL_EMPLOYEES",
  "EMP Memberships": "EMP_MEMBERSHIPS",
  "EMP Notes": "EMP_NOTES",
  "EMP Notifications": "EMP_NOTIFICATIONS",
  "EMP Invoices": "EMP_INVOICES",
  "EMP Payment Option": "EMP_PAYMENT_OPTION",
  "EMP Employee Notifications": "EMP_EMPLOYEE_NOTIFICATIONS",

  // Broker Portal Module
  "Broker Portal": "BROKER_PORTAL",

  // CRM Module
  "CRM - Retail": "CRM_RETAIL",
  "CRM - Employer": "CRM_EMPLOYER",
  "Lead Sources": "CRM_LEAD_SOURCES",

  // Inventory Module
  Inventory: "INVENTORY",
  "Pharmacy Inventory": "INVENTORY_PHARMACY",
  "Expiring Medications": "INVENTORY_EXPIRING_MEDICATIONS",

  // Reports Module
  "Clinic/Provider Activity": "REPORTS_CLINIC_ACTIVITY",
  "Health Trends/Diagnosis": "REPORTS_HEALTH_TRENDS",
  "Utilization/Engagement": "REPORTS_UTILIZATION",
  "Churn Reports": "REPORTS_CHURN",
  "Invoices Report": "REPORTS_INVOICES",
  "Savings Report": "REPORTS_SAVINGS",

  // Other Features
  "Referrals & Orders": "REFERRALS_ORDERS",
  "Appointment Status/Type": "APPOINTMENT_STATUS_TYPE",
  "Patient Categories": "PATIENT_CATEGORIES",
  Membership: "MEMBERSHIP",
  Enrollments: "ENROLLMENTS",
  Terminations: "TERMINATIONS",
  "Outstanding Invoices": "OUTSTANDING_INVOICES",
  "Received Payments": "RECEIVED_PAYMENTS",
  "Deferred Revenue": "DEFERRED_REVENUE",
  "Bad Debts": "BAD_DEBTS",
  "Coupons In Use": "COUPONS_IN_USE",
  "Broker Management": "BROKER_MANAGEMENT",
  Broker: "BROKER",
  Affiliates: "AFFILIATES",
  "Affiliate Provider Management": "AFFILIATE_PROVIDER_MANAGEMENT",
  "Abandoned Sign-Up Attempts": "ABANDONED_SIGNUP_ATTEMPTS",
} as const;

// Navigation permission mapping
export const NAVIGATION_PERMISSION_MAPPING = {
  // Main modules
  ADMIN: ["Admin Dashboard"],
  EHR: ["EHR Dashboard"],
  BILLING: ["Billing"],
  EMPLOYER_MANAGEMENT: ["Employer Management"],
  CRM_RETAIL: ["CRM - Retail"],
  CRM_EMPLOYER: ["CRM - Employer"],
  INVENTORY: ["Inventory"],
  REPORTS: [
    "Clinic/Provider Activity",
    "Health Trends/Diagnosis",
    "Utilization/Engagement",
  ],
  BROKER_PORTAL: ["Broker Portal"],
  AFFILIATE_PORTAL: ["Affiliate Portal"],

  // Admin sub-modules
  ADMIN_DASHBOARD: ["Admin Dashboard"],
  ADMIN_USERS: ["Admin Account User"],
  ADMIN_FORM_BUILDER: ["Admin Form Builder"],
  ADMIN_MEMBERSHIP: ["Admin Membership Plan"],
  ADMIN_REPORTS: ["Admin Reports"],
  ADMIN_EDUCATION_MATERIALS: ["Admin Educational Material"],
  ADMIN_AFFILIATE: ["Affiliates"],
  ADMIN_SETTINGS: ["Admin Settings"],
  ADMIN_PAYMENT_SOURCE: ["Admin Account Payment source"],
  ADMIN_ROLES_PERMISSIONS: ["Admin Roles and Permissions"],
  ADMIN_NOTIFICATIONS: ["Admin Notifications"],
  ADMIN_CHARGE_TAXES: ["Admin Charge and Taxes"],
  ADMIN_PROVIDERS: ["Admin Providers"],
  ADMIN_LOCATIONS: ["Admin Locations"],
  ADMIN_LOCATION_GROUPS: ["Admin Location Groups"],
  ADMIN_MAPS: ["Admin Maps"],
  ADMIN_COUPONS: ["Admin Coupons - Retail patient"],
  ADMIN_TERMS_CONDITIONS: ["Admin Terms and conditions"],
  ADMIN_AUDIT_LOG: ["Admin Audit Log"],
  ADMIN_CANCELLATION_REASON: ["Admin Cancellation Reason"],
  ADMIN_COVERAGE_PLAN: ["Admin Coverage Plan"],
  ADMIN_INVOICING_AHEAD: ["Admin Invoicing Ahead of Time"],

  // Affiliate Portal sub-modules
  AFFILIATE_DASHBOARD: ["Affiliate Dashboard"],
  AFFILIATE_PROVIDERS: ["Affiliate Providers"],
  AFFILIATE_LOCATIONS: ["Affiliate Locations"],
  AFFILIATE_LOCATION_GROUPS: ["Affiliate Location Groups"],
  AFFILIATE_MAPS: ["Affiliate Maps"],
  AFFILIATE_CHARGE_CATEGORIES: ["Affiliate Charge Categories"],
  AFFILIATE_SETTINGS: ["Affiliate Settings"],
  AFFILIATE_BILLS: ["Affiliate Bills"],
  AFFILIATE_PATIENTS: ["Affiliate Patients"],

  // EHR sub-modules
  EHR_DASHBOARD: ["EHR Dashboard"],
  EHR_PATIENTS: ["EHR Patients"],
  EHR_SCHEDULING: ["EHR Scheduling"],
  EHR_TASKS: ["EHR Tasks"],
  EHR_REFERRAL: ["EHR Referral"],
  EHR_ADMIN: ["EHR Admin"],
  EHR_PATIENT_PROFILE: ["EHR Patient Profile"],
  EHR_PATIENT_DASHBOARD: ["EHR Patient Dashboard"],
  EHR_PATIENT_EDUCATION: ["EHR Patient Educational Material"],
  EHR_PATIENT_ALLERGIES: ["EHR Patient allergies"],
  EHR_PATIENT_VITALS: ["EHR Patient Vitals"],
  EHR_PATIENT_MEDICATIONS: ["EHR Patient Medications"],
  EHR_PATIENT_DIAGNOSIS: ["EHR Patient Diagnosis"],
  EHR_PATIENT_VACCINES: ["EHR Patient Vaccines"],
  EHR_PATIENT_IMAGING: ["EHR Patient Imaging"],
  EHR_PATIENT_LABS: ["EHR Patient Labs"],
  EHR_PATIENT_REFERRALS: ["EHR Patient Referrals"],
  EHR_PATIENT_DOCUMENTS: ["EHR Patient Documents"],
  EHR_PATIENT_MESSAGES: ["EHR Patient Messages"],
  EHR_PATIENT_TASKS: ["EHR Patient Tasks"],
  EHR_PATIENT_HISTORY: ["EHR Patient History"],
  EHR_PATIENT_VISIT_DETAILS: ["EHR Patient Visit Details"],
  EHR_PATIENT_TIMELINE: ["EHR Patient timeline"],
  EHR_PATIENT_DRUG_TOLERANCE: ["EHR Patient Drug Tolerance"],
  EHR_PATIENT_PHARMACIES: ["EHR Patient Pharmacies"],
  EHR_PATIENT_INVOICE: ["EHR Patient Invoice"],
  EHR_PATIENT_MEMBERSHIP: ["EHR Patient Membership Details"],
  EHR_APPOINTMENT_TYPE: ["EHR Appointment type"],
  EHR_AVAILABILITY: ["EHR Availabillity"],
  EHR_STATUS_CONFIG: ["EHR Status Configuration"],
  EHR_CUSTOMIZABLE_INFO_SHARING: ["EHR Customizable Information Sharing"],
  EHR_CARE_PLANS: ["EHR Patient Educational Material"],
  EHR_SETTINGS: ["EHR Settings"],

  // Billing sub-modules
  BILLING_DASHBOARD: ["Billing"],
  BILLING_PATIENTS: ["Billing Patients"],
  BILLING_EMPLOYERS: ["Billing Employers"],
  BILLING_BASIC_DETAILS: ["Billing Basic Details"],
  BILLING_EMPLOYEE_INVOICES: ["Billing Employee Invoices"],
  BILLING_CREDITS: ["Billing Credits"],
  BILLING_MEMBERSHIPS: ["Billing Memberships"],
  BILLING_EMPLOYEE_PAYMENT_OPTION: ["Billing Employee Payment Option"],
  BILLING_EMPLOYEE_NOTES: ["Billing employee Notes"],
  BILLING_EMPLOYEE_NOTIFICATIONS: ["Billing employee Notifications"],
  BILLING_INVOICES: ["Billing Invoices"],
  BILLING_SETTINGS: ["Billing Basic Details"],
  BILLING_INVOICE_SETTINGS: ["Billing Invoice Settings"],
  BILLING_PAYMENT_OPTION: ["Billing Payment Option"],
  BILLING_PLANS: ["Billing Plans"],
  BILLING_PRICING: ["Billing Pricing"],
  BILLING_NOTIFICATIONS: ["Billing Notifications"],
  BILLING_NOTES: ["Billing Notes"],
  BILLING_BROKERS: ["Billing Brokers"],
  BILLING_RETAIL: ["Billing Retail"],

  // Employer Management sub-modules
  EM_DASHBOARD: ["EM Dashboard"],
  EM_EMPLOYERS: ["EM Employers"],
  EM_ALL_EMPLOYEES: ["EM All employees"],
  EM_BASIC_DETAILS: ["EM Basic Details"],
  EM_INVOICES: ["EM Invoices"],
  EM_CREDITS: ["EM Credits"],
  EM_MEMBERSHIPS: ["EM Memberships"],
  EM_EMPLOYEE_PAYMENT_OPTION: ["EM Employee Payment Option"],
  EM_NOTES: ["EM Notes"],
  EM_EMPLOYEE_NOTIFICATIONS: ["EM Employee Notifications"],
  EM_SETTINGS: ["EM Basic Details"],
  EM_ACCOUNT_DETAILS: ["EM Account Details"],
  EM_INVOICE_SETTINGS: ["EM Invoice Settings"],
  EM_PAYMENT_OPTION: ["EM Payment Option"],
  EM_PLANS: ["EM Plans"],
  EM_PRICING: ["EM Pricing"],
  EM_NOTIFICATIONS: ["EM Notifications"],
  EM_BROKERS: ["EM Brokers"],
  EM_EMPLOYEE_UPLOADS: ["EM Employee Uploads"],
  EM_ONLINE_SIGNUP: ["EM Online Signup"],
  EM_EMPLOYEES: ["EM Employees"],

  // Employer Portal sub-modules
  EMP_DASHBOARD: ["EMP Dashboard"],
  EMP_ALL_EMPLOYEES: ["EMP All employees"],
  EMP_MEMBERSHIPS: ["EMP Memberships"],
  EMP_EMPLOYEE_MEMBERSHIPS: ["EMP Employee Memberships"],
  EMP_NOTES: ["EMP Notes"],
  EMP_NOTIFICATIONS: ["EMP Notifications"],
  EMP_INVOICES: ["EMP Invoices"],
  EMP_PAYMENT_OPTION: ["EMP Payment Option"],
  EMP_EMPLOYEE_NOTIFICATIONS: ["EMP Employee Notifications"],
  EMP_EMPLOYEE_UPLOADS: ["EM Employee Uploads"],
  EMP_SETTINGS: ["EM Basic Details"],
} as const;

// Custom permission checking for special cases
export const hasCustomPermission = (
  permissions: Permissions | null,
  featureKey: string,
  user: any,
  moduleType?: string,
): boolean => {
  // Handle EHR Settings custom permission logic
  if (featureKey === "EHR_Settings_Custom") {
    const canView = (featureName: string) =>
      hasPermission(permissions, featureName, "viewAll") ||
      hasPermission(permissions, featureName, "view");

    return canView("EHR Setting");
  } else if (featureKey === "EMP Settings custom") {
    const hasAdminListPermission = useHasAllPermissions(
      ["EMP Admin List", "EMP Notification Preferances"]["viewAll"],
    );
  }

  // Handle All Employees navigation permission logic
  if (featureKey === "All_Employees_Navigation") {
    const currentModuleType = moduleType || "employer-management";

    if (currentModuleType === "billing") {
      // For billing: user needs view permission on main (Billing Patients) - this is mandatory
      const hasMainPermission = hasPermission(
        permissions,
        "Billing Patients",
        "view",
      );

      // If no main permission, return false immediately
      if (!hasMainPermission) {
        return false;
      }

      // Only check sub permissions if main permission exists
      const hasAnySubPermission = hasAnyPermission(
        permissions,
        [
          "Billing Basic Details",
          "Billing Employee Invoices",
          "Billing Credits",
          "Billing Memberships",
          "Billing Employee Payment Option",
          "Billing employee Notes",
          "Billing employee Notifications",
        ],
        "viewAll",
      );
      return hasAnySubPermission;
    } else if (currentModuleType === "employer-management") {
      // For employer management: user needs view permission on main (EM All employees) - this is mandatory
      const hasMainPermission = hasPermission(
        permissions,
        "EM All employees",
        "view",
      );

      // If no main permission, return false immediately
      if (!hasMainPermission) {
        return false;
      }

      // Only check sub permissions if main permission exists
      const hasAnySubPermission = hasAnyPermission(
        permissions,
        [
          "EM Basic Details",
          "EM Invoices",
          "EM Credits",
          "EM Memberships",
          "EM Employee Payment Option",
          "EM Notes",
          "EM Employee Notifications",
        ],
        "viewAll",
      );
      return hasAnySubPermission;
    } else if (currentModuleType === "employer-portal") {
      // For employer portal: user needs view permission on main (EMP All employees) - this is mandatory
      const hasMainPermission = hasPermission(
        permissions,
        "EMP All employees",
        "view",
      );

      // If no main permission, return false immediately
      if (!hasMainPermission) {
        return false;
      }

      const hasAnySubPermission = hasAnyPermission(
        permissions,
        [
          "EMP Basic Details",
          "EMP Memberships",
          "EMP Notes",
          "EMP Invoices",
          "EMP Payment Option",
          "EMP Employee Notifications",
        ],
        "viewAll",
      );
      return hasAnySubPermission;
    }
  }

  // Default to false for unknown custom permissions
  return false;
};

// Permission checking utilities
export const hasPermission = (
  permissions: Permissions | null,
  featureKey: string,
  action: keyof Permission = "view",
  user?: any, // Add user parameter for custom permission checks
): boolean => {
  // Check for custom permissions first
  if (featureKey.includes("Custom") || featureKey === "EHR_Settings_Custom") {
    return hasCustomPermission(permissions, featureKey, user);
  }

  if (!permissions) return false;

  const permission = permissions[featureKey];
  if (!permission) return false;

  return permission[action] || false;
};

export const hasAnyPermission = (
  permissions: Permissions | null,
  featureKeys: string[],
  action: keyof Permission = "view",
): boolean => {
  if (!permissions) return false;

  return featureKeys.some((key) => hasPermission(permissions, key, action));
};

export const hasAllPermissions = (
  permissions: Permissions | null,
  featureKeys: string[],
  action: keyof Permission = "view",
): boolean => {
  if (!permissions) return false;

  return featureKeys.some((key) => hasPermission(permissions, key, action));
};

// Navigation permission checking
export const canAccessModule = (
  permissions: Permissions | null,
  moduleKey: string,
): boolean => {
  const requiredPermissions = NAVIGATION_PERMISSION_MAPPING[moduleKey];
  if (!requiredPermissions) return true; // If no mapping, allow access

  return hasAnyPermission(permissions, requiredPermissions, "view");
};

export const canAccessSubModule = (
  permissions: Permissions | null,
  subModuleKey: string,
): boolean => {
  const requiredPermissions = NAVIGATION_PERMISSION_MAPPING[subModuleKey];
  if (!requiredPermissions) return true; // If no mapping, allow access

  return hasAnyPermission(permissions, requiredPermissions, "view");
};

// Custom hooks for permission checking
export const usePermissions = () => {
  return useSelector((state: RootState) => state.auth.permissions);
};

export const useHasPermission = (
  featureKey: string,
  action: keyof Permission = "view",
) => {
  const permissions = usePermissions();
  const user = useSelector((state: RootState) => state.auth.user);
  return hasPermission(permissions, featureKey, action, user);
};

export const useHasAnyPermission = (
  featureKeys: string[],
  action: keyof Permission = "view",
) => {
  const permissions = usePermissions();
  return hasAnyPermission(permissions, featureKeys, action);
};

export const useHasAllPermissions = (
  featureKeys: string[],
  action: keyof Permission = "view",
) => {
  const permissions = usePermissions();
  return hasAllPermissions(permissions, featureKeys, action);
};

export const useCanAccessModule = (moduleKey: string) => {
  const permissions = usePermissions();
  return canAccessModule(permissions, moduleKey);
};

export const useCanAccessSubModule = (subModuleKey: string) => {
  const permissions = usePermissions();
  return canAccessSubModule(permissions, subModuleKey);
};

export const useCanNavigateAllEmployees = (moduleType?: string) => {
  const permissions = usePermissions();
  const user = useSelector((state: RootState) => state.auth.user);
  return hasCustomPermission(
    permissions,
    "All_Employees_Navigation",
    user,
    moduleType,
  );
};

// Permission action types
export const PERMISSION_ACTIONS = {
  VIEW: "view",
  VIEW_ALL: "viewAll",
  CREATE: "create",
  UPDATE: "update",
  DELETE: "delete",
} as const;

export type PermissionAction = keyof Permission;
